import React from "react";
import _ from "lodash";

import Select from "react-select";
import TextAreaWithJsonCheck from "components/common/TextAreaWithJsonCheck";
import ClickToCopyWrapper from "components/common/ClickToCopyWrapper";
import RadioControl from "components/common/RadioControl";
import Checkbox from "components/common/Checkbox";

import PriceTargetingControl from "./PriceTargetingControl";
import AdvertiserAndBrandsSelector from "./AdvertiserAndBrandsSelector";
import SensitiveCategoriesSelector from "./SensitiveCategoriesSelector";
import VideoOptionControl from "./VideoOptionControl";

import { GAM_CREATIVE_TYPES } from "./Constants";
import { TARGETING_OP, TARGETING_TYPE } from "./Constants";
import { DEMAND_SOURCE_TYPE } from "constants/DemandSources";
import { getDemandSourceType } from "components/network/pricing-rules/DataHelpers";

const inputClass =
  "bg-white flex w-full border border-gray-400 rounded py-1 px-3 appearance-none leading-normal focus:outline-none focus:border-blue-400 focus:shadow-inner hover:border-gray-500";

const PRICE_TYPE_NAME = {
  FLOOR: 0,
  TARGET_CPM: 1,
  // LGO: 2, // not an option for brand price settings
};

class BrandPriceSettingControl extends React.PureComponent {
  constructor(props) {
    super(props);

    const { currentBrandPriceSetting } = props;
    const { price = "", priceType = "" } = currentBrandPriceSetting;
    const targeting = _.get(currentBrandPriceSetting, ["targeting"], {
      // sizes: {
      //   includes: [],
      // },
    });
    // creativeType: no key = "ALL". always only have a single item
    const creativeType = _.get(currentBrandPriceSetting, [
      "targeting",
      "creativeTypes",
      0,
    ]);

    this.state = {
      price,
      priceType,
      targeting,
      creativeType: creativeType || "ALL",

      errMsg: null,
    };

    this.handleDemandSourceChanged = this.handleDemandSourceChanged.bind(this);
    this.handlePriceTypeChanged = this.handlePriceTypeChanged.bind(this);
    this.handlePriceChanged = this.handlePriceChanged.bind(this);
    this.handleCreativeTypeChanged = this.handleCreativeTypeChanged.bind(this);
    this.handleAdvertiserAndBrandsChanged =
      this.handleAdvertiserAndBrandsChanged.bind(this);
    this.handleAddBrandPriceSetting =
      this.handleAddBrandPriceSetting.bind(this);
    this.handleCustomTargetingChanged =
      this.handleCustomTargetingChanged.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
  }

  handleDemandSourceChanged(newVal) {
    const { targeting } = this.state;
    let newTargeting = {
      ...targeting,
    };

    if (!_.isNil(newVal)) {
      newTargeting["demandSources"] = newVal;
    } else {
      newTargeting = _.omit(newTargeting, "demandSources");
    }

    this.setState({ targeting: newTargeting });
  }

  handlePriceTypeChanged(priceType) {
    this.setState({ priceType });
  }

  handlePriceChanged(e) {
    const price = e.target.value ? parseFloat(e.target.value) : "";
    this.setState({ price });
  }

  handleCreativeTypeChanged(ct) {
    this.setState({ creativeType: ct });
  }

  handleAdvertiserAndBrandsChanged(selectedOptions) {
    if (!selectedOptions || _.isEmpty(selectedOptions)) {
      const newTargeting = _.omit(this.state.targeting, "advertisersAndBrands");
      this.setState({ targeting: newTargeting });
      return;
    }

    let optionsByAdvertiser = _(selectedOptions)
      .groupBy("advertiserId")
      .reduce((result, options) => {
        const advertiserId = _.first(options).advertiserId;
        let brandIds = [];
        _.forEach(options, (option) => {
          if (option.type === "BRAND") {
            brandIds.push(option.value);
          }
        });
        result.push({
          advertiserId,
          brandIds,
        });
        return result;
      }, []);
    const targeting = this.state.targeting;
    const newTargeting = {
      ...targeting,
      advertisersAndBrands: optionsByAdvertiser,
    };
    this.setState({ targeting: newTargeting });

    // "advertisersAndBrands": [
    //   // Include unclassified advertisers or brands
    //     // advertiserId: "0", brandIds: []

    //   {
    //     !"advertiserId": string, // \d+
    //     !"brandIds": [string] // \d+
    //   },
    //   ...
    // ]
  }

  handleAddBrandPriceSetting() {
    const { priceType, price, targeting, creativeType } = this.state;

    // Demand Source validation
    // if priceType is CPM (value: 1), `demandSources` must be empty (no set)
    const demandSources = _.get(targeting, "demandSources", null);
    if (
      priceType === PRICE_TYPE_NAME.TARGET_CPM &&
      _.isPlainObject(demandSources) &&
      !_.isEmpty(demandSources)
    ) {
      return this.setState({
        errMsg: "Target CPMs are not available when selecting specific bidders",
      });
    }

    const idsListValid = _.every(
      demandSources,
      (ds) =>
        ds.all === true || (ds.all === false && ds.ids && ds.ids.length > 0)
    );
    if (!idsListValid) {
      return this.setState({
        errMsg: "Please ensure all Demand Sources have valid IDs list",
      });
    }

    // only one type of targeting is required!
    // Creative, Sizes or Advertiser and Brands
    if (
      creativeType === null ||
      creativeType === undefined ||
      creativeType === ""
    ) {
      return this.setState({ errMsg: "Creative Type is required" });
    }

    // sizes or advertiser is required
    if (creativeType === "ALL") {
      const sizesIncludes = _.get(targeting, ["sizes", "includes"], []);
      const sizesExcludes = _.get(targeting, ["sizes", "excludes"], []);
      const hasSizes = sizesIncludes.length > 0 || sizesExcludes.length > 0;

      const advertisersAndBrands = _.get(targeting, "advertisersAndBrands", []);
      const hasAdvertisers = advertisersAndBrands.length > 0;

      const isEmptyDemandSource =
        _.isEmpty(demandSources) || _.isNil(demandSources);

      if (isEmptyDemandSource && !hasSizes && !hasAdvertisers) {
        return this.setState({
          errMsg: "Need Sizes or Advertiser and Brands Targeting",
        });
      }
    }

    // advertisersAndBrands max is 49
    // sizes max is 250
    const advertisersAndBrands = _.get(targeting, "advertisersAndBrands", []);
    if (advertisersAndBrands.length > 49) {
      return this.setState({
        errMsg: "Cannot have more than 49 Advertiser and Brands Targeting",
      });
    }
    const sizesIncludes = _.get(targeting, ["sizes", "includes"], []);
    const sizesExcludes = _.get(targeting, ["sizes", "excludes"], []);
    if (sizesIncludes.length > 250 || sizesExcludes.length > 250) {
      return this.setState({
        errMsg: "Cannot have more than 250 Sizes Targeting",
      });
    }

    // validate price related fields
    if (priceType === null || priceType === undefined || priceType === "") {
      return this.setState({ errMsg: "Price Type is required" });
    }

    if (price === null || price === undefined || price === "") {
      return this.setState({ errMsg: "Price is required" });
    }

    this.setState({ errMsg: null });
    let newSetting = {
      price,
      priceType,
      targeting,
    };

    if (creativeType !== "ALL") {
      newSetting.targeting.creativeTypes = [creativeType]; // array with single item
    } else {
      newSetting = _.omit(newSetting, "targeting.creativeTypes");
    }

    if (creativeType === GAM_CREATIVE_TYPES["Display"]) {
      newSetting = _.omit(newSetting, "targeting.videoOption");
    }

    // Delete demand sources if it is {}
    if (_.isPlainObject(demandSources) && _.isEmpty(demandSources)) {
      newSetting = _.omit(newSetting, "targeting.demandSources");
    }

    this.props.handleAddSetting(newSetting);
  }

  handleCustomTargetingChanged(newVal, targetingKey) {
    const targeting = this.state.targeting;
    let newTargeting = {
      ...targeting,
    };

    if (!_.isNil(newVal)) {
      newTargeting[targetingKey] = newVal;
    } else {
      newTargeting = _.omit(newTargeting, targetingKey);
    }
    this.setState({ targeting: newTargeting });
  }

  handleCancel() {
    this.setState({
      price: "",
      priceType: null,
      targeting: {},

      errMsg: null,
    });
    this.props.handleCancel();
  }

  render() {
    const { currency, networkId, demandSource } = this.props;
    const { price, priceType, creativeType, targeting, errMsg } = this.state;

    return (
      <div className="rounded bg-white p-2 text-sm">
        <div className="border-b mb-4 text-sm">New Brand Price Setting</div>

        {/* Demand Sources */}
        <div className="mb-4">
          <div className="font-bold text-gray-800">Demand Sources </div>
          <div className="text-xs text-gray-700">
            Target CPMs are not available when selecting specific bidders
          </div>
          <DemandSourceControl
            targeting={targeting["demandSources"]}
            demandSource={demandSource}
            handleChanged={this.handleDemandSourceChanged}
          ></DemandSourceControl>
        </div>

        {/* Advertisers and Brands */}
        <div className="mb-4">
          <label className="block font-bold text-gray-800">
            Advertisers and Brands Targeting
          </label>
          <AdvertiserAndBrandsControl
            networkId={networkId}
            targeting={targeting["advertisersAndBrands"]}
            handleChanged={this.handleAdvertiserAndBrandsChanged}
          ></AdvertiserAndBrandsControl>
        </div>

        {/* Sizes */}
        <div className="mb-4">
          <label className="block font-bold text-gray-800">
            Sizes Targeting
          </label>
          <PriceTargetingControl
            networkId={networkId}
            noneText="Any Size"
            targetingType={TARGETING_TYPE.SIZES}
            targeting={targeting["sizes"]}
            handleTargetingChanged={(val) =>
              this.handleCustomTargetingChanged(val, "sizes")
            }
          ></PriceTargetingControl>
        </div>

        {/* Sensitive Categories */}
        <div className="mb-4">
          <label className="block font-bold text-gray-800">
            Sensitive Categories
          </label>
          <CustomTargetingControl
            noneText="Any Sensitive Category"
            networkId={networkId}
            targetingType={TARGETING_TYPE.SENSITIVE_CATEGORIES}
            targeting={targeting["sensitiveCategories"]}
            handleTargetingChanged={(val) =>
              this.handleCustomTargetingChanged(val, "sensitiveCategories")
            }
          ></CustomTargetingControl>
        </div>

        {/* Creative Types */}
        <div className="mb-4">
          <div className="block font-bold text-gray-800">
            GAM Creative Types
          </div>
          <div className="flex items-center gap-4">
            <label
              htmlFor={`radio_brand_creative_type_all`}
              className="cursor-pointer"
            >
              <input
                id={`radio_brand_creative_type_all`}
                type="radio"
                name={`radio_brand_creative_type`}
                value={"ALL"}
                checked={creativeType === "ALL"}
                onChange={() => this.handleCreativeTypeChanged("ALL")}
              />{" "}
              Any Creative Type
            </label>

            <label
              htmlFor={`radio_brand_creative_type_display`}
              className="cursor-pointer"
            >
              <input
                id={`radio_brand_creative_type_display`}
                type="radio"
                name={`radio_brand_creative_type`}
                value={GAM_CREATIVE_TYPES["Display"]}
                checked={creativeType === GAM_CREATIVE_TYPES["Display"]}
                onChange={() =>
                  this.handleCreativeTypeChanged(GAM_CREATIVE_TYPES["Display"])
                }
              />{" "}
              Display Only
            </label>

            <label
              htmlFor={`radio_brand_creative_type_video`}
              className="cursor-pointer"
            >
              <input
                id={`radio_brand_creative_type_video`}
                type="radio"
                name={`radio_brand_creative_type`}
                value={GAM_CREATIVE_TYPES["Video"]}
                checked={creativeType === GAM_CREATIVE_TYPES["Video"]}
                onChange={() =>
                  this.handleCreativeTypeChanged(GAM_CREATIVE_TYPES["Video"])
                }
              />{" "}
              Video Only
            </label>
          </div>
        </div>

        {/* Video Options */}
        {creativeType !== GAM_CREATIVE_TYPES["Display"] && (
          <div className="mb-4">
            <label className="block font-bold text-gray-800">
              Video Options
            </label>
            <CustomTargetingControl
              targetingType={TARGETING_TYPE.VIDEO_OPTION}
              targeting={targeting["videoOption"]}
              handleTargetingChanged={(val) =>
                this.handleCustomTargetingChanged(val, "videoOption")
              }
            ></CustomTargetingControl>
          </div>
        )}

        {/* Pricing */}
        <div className="mb-4">
          <div className="block font-bold text-gray-800">Price Type</div>
          <div className="flex items-center gap-4">
            <label
              htmlFor={`radio_brand_pricing_floor`}
              className="cursor-pointer"
            >
              <input
                id={`radio_brand_pricing_floor`}
                type="radio"
                name={`radio_brand_pricing`}
                value={PRICE_TYPE_NAME.FLOOR}
                checked={priceType === PRICE_TYPE_NAME.FLOOR}
                onChange={() =>
                  this.handlePriceTypeChanged(PRICE_TYPE_NAME.FLOOR)
                }
              />{" "}
              Floor
            </label>

            <label
              htmlFor={`radio_brand_pricing_targetcpm`}
              className="cursor-pointer"
            >
              <input
                id={`radio_brand_pricing_targetcpm`}
                type="radio"
                name={`radio_brand_pricing`}
                value={PRICE_TYPE_NAME.TARGET_CPM}
                checked={priceType === PRICE_TYPE_NAME.TARGET_CPM}
                onChange={() =>
                  this.handlePriceTypeChanged(PRICE_TYPE_NAME.TARGET_CPM)
                }
              />{" "}
              Target CPM
            </label>
          </div>
        </div>

        <div className="mb-4">
          <label
            className="block font-bold text-gray-800"
            htmlFor="branded_price"
          >
            Price in {currency}
          </label>
          <input
            className={inputClass}
            id="branded_price"
            type="number"
            value={price}
            step={0.01}
            min={0}
            style={{ width: "200px" }}
            onChange={this.handlePriceChanged}
          />
        </div>

        <div className="border-t mb-2 mt-1 flex items-center justify-end px-2 py-2">
          <div className="text-red-600">{errMsg}</div>
          <button
            type="button"
            className="px-4 py-1 text-blue-700 hover:underline"
            onClick={this.handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded shadow bg-blue-200 px-4 py-1 font-semibold text-blue-700 hover:bg-blue-300"
            onClick={this.handleAddBrandPriceSetting}
          >
            Add Brand Price Setting
          </button>
        </div>
      </div>
    );
  }
}

const DEFAULT_ADVERTISERS_AND_BRANDS = [
  {
    advertiserId: "0",
    brandIds: [],
  },
  {
    advertiserId: "111",
    brandIds: ["222", "333"],
  },
];

class AdvertiserAndBrandsControl extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      controller: props.targeting ? "includes" : "NONE", // "NONE", "includes"
      controlType: "JSON", // "SELECTOR"
      jsonString: props.targeting ? JSON.stringify(props.targeting) : "",

      finalTargeting: null,
      verifyErrMsg: null,
    };

    this.handleControllerChanged = this.handleControllerChanged.bind(this);
    this.handleControlTypeChanged = this.handleControlTypeChanged.bind(this);
    this.handleInputChanged = this.handleInputChanged.bind(this);
    this.handleVerify = this.handleVerify.bind(this);
    this.handleClearAdvertisersAndBrands =
      this.handleClearAdvertisersAndBrands.bind(this);
  }

  // when control type changes, update jsonString or selector to the same value
  componentDidUpdate(prevProps) {
    if (prevProps.targeting !== this.props.targeting) {
      this.setState({
        jsonString: this.props.targeting
          ? JSON.stringify(this.props.targeting)
          : "",
      });
    }
  }

  handleInputChanged(inputValue) {
    this.setState({ jsonString: inputValue });
  }

  handleControllerChanged(controller) {
    this.setState({
      controller,
    });

    if (controller === "NONE") {
      this.handleClearAdvertisersAndBrands();
    }
  }

  handleControlTypeChanged(type) {
    this.setState({
      controlType: type,
      finalTargeting: null,
      verifyErrMsg: null,
    });
  }

  handleVerify() {
    this.setState({ finalTargeting: null });

    setTimeout(() => {
      try {
        this.setState({
          finalTargeting: JSON.parse(this.state.jsonString),
          verifyErrMsg: null,
        });
        this.props.handleChanged(this.state.finalTargeting);
      } catch (err) {
        this.setState({ verifyErrMsg: err.toString() });
      }
    });
  }

  handleClearAdvertisersAndBrands() {
    this.setState({ jsonString: "" });
    this.props.handleChanged(null);
  }

  render() {
    const { networkId, targeting, handleChanged } = this.props;
    const {
      controller,
      controlType,
      jsonString,
      finalTargeting,
      verifyErrMsg,
    } = this.state;
    const defaultValue = JSON.stringify(DEFAULT_ADVERTISERS_AND_BRANDS);

    return (
      <>
        <div className="flex items-center gap-4">
          <label
            htmlFor={`radio_advb_controller_none`}
            className="cursor-pointer"
          >
            <input
              id={`radio_advb_controller_none`}
              type="radio"
              name={`radio_advb_controller`}
              value={"NONE"}
              checked={controller === "NONE"}
              onChange={() => this.handleControllerChanged("NONE")}
            />{" "}
            Any Advertiser or Brand
          </label>

          <label
            htmlFor={`radio_advb_controller_includes`}
            className="cursor-pointer"
          >
            <input
              id={`radio_advb_controller_includes`}
              type="radio"
              name={`radio_advb_controller`}
              value={"includes"}
              checked={controller === "includes"}
              onChange={() => this.handleControllerChanged("includes")}
            />{" "}
            Includes
          </label>
        </div>
        {controller === "includes" && (
          <div className="border mt-1 px-2 py-1">
            <div className="flex items-center gap-4">
              <label
                htmlFor={`radio_advb_control_type_json`}
                className="cursor-pointer"
              >
                <input
                  id={`radio_advb_control_type_json`}
                  type="radio"
                  name={`radio_advb_control_type`}
                  value={"JSON"}
                  checked={controlType === "JSON"}
                  onChange={() => this.handleControlTypeChanged("JSON")}
                />{" "}
                JSON
              </label>

              <label
                htmlFor={`radio_advb_control_type_selector`}
                className="cursor-pointer"
              >
                <input
                  id={`radio_advb_control_type_selector`}
                  type="radio"
                  name={`radio_advb_control_type`}
                  value={"SELECTOR"}
                  checked={controlType === "SELECTOR"}
                  onChange={() => this.handleControlTypeChanged("SELECTOR")}
                />{" "}
                Selector
              </label>
            </div>
            {controlType === "JSON" ? (
              <>
                <div className="flex items-center justify-between text-xs text-gray-700">
                  <div style={{ width: "90%" }}>ex. {defaultValue} </div>
                  <ClickToCopyWrapper
                    copyText={defaultValue}
                  ></ClickToCopyWrapper>
                </div>
                <TextAreaWithJsonCheck
                  rows={6}
                  inputValue={jsonString}
                  handleInputChanged={this.handleInputChanged}
                ></TextAreaWithJsonCheck>

                <div className="items flex gap-2 pt-2">
                  <button
                    type="button"
                    className="rounded shadow mb-2 bg-gray-200 px-4 py-1 font-semibold text-gray-700 hover:bg-gray-300"
                    onClick={this.handleClearAdvertisersAndBrands}
                  >
                    Clear advertisers and brands
                  </button>
                  <button
                    type="button"
                    className="rounded shadow mb-2 bg-teal-200 px-4 py-1 font-semibold text-teal-700 hover:bg-teal-300"
                    onClick={this.handleVerify}
                  >
                    Verify Advertisers and Brands JSON
                  </button>
                </div>
                <div className="pt-2">
                  {verifyErrMsg && (
                    <div className="text-red-700">{verifyErrMsg}</div>
                  )}
                  {finalTargeting && (
                    <AdvertiserAndBrandsSelector
                      networkId={networkId}
                      targeting={finalTargeting}
                      isDisabled={true}
                      // handleTargetingChanged={handleChanged}
                    ></AdvertiserAndBrandsSelector>
                  )}
                </div>
              </>
            ) : (
              <AdvertiserAndBrandsSelector
                networkId={networkId}
                targeting={targeting}
                handleTargetingChanged={handleChanged}
              ></AdvertiserAndBrandsSelector>
            )}
          </div>
        )}
      </>
    );
  }
}

class CustomTargetingControl extends React.PureComponent {
  constructor(props) {
    super(props);

    const { targeting } = props;

    this.state = {
      radioValue: _.isNil(targeting)
        ? TARGETING_OP.NONE
        : TARGETING_OP.INCLUDES,
    };

    this.handleChangeRadio = this.handleChangeRadio.bind(this);
  }

  handleChangeRadio(value) {
    this.setState({
      radioValue: value,
    });

    if (value === TARGETING_OP.NONE) {
      this.props.handleTargetingChanged(null);
    }
  }

  render() {
    const {
      targetingType,
      networkId,
      targeting,
      handleTargetingChanged,
      noneText = "None",
      includesText = "Includes",
    } = this.props;
    const { radioValue } = this.state;

    let TargetingSelector = "";
    switch (targetingType) {
      case TARGETING_TYPE.SENSITIVE_CATEGORIES: {
        TargetingSelector = (
          <SensitiveCategoriesSelector
            networkId={networkId}
            targeting={targeting}
            onChange={handleTargetingChanged}
          ></SensitiveCategoriesSelector>
        );
        break;
      }

      case TARGETING_TYPE.VIDEO_OPTION: {
        TargetingSelector = (
          <VideoOptionControl
            targeting={targeting}
            onChange={handleTargetingChanged}
          ></VideoOptionControl>
        );
        break;
      }

      default: {
        TargetingSelector = "Selector not implemented";
      }
    }

    return (
      <>
        <div className="flex items-center gap-4">
          <label
            htmlFor={`${targetingType}_radio_none`}
            className="cursor-pointer"
          >
            <input
              id={`${targetingType}_radio_none`}
              type="radio"
              name={`${targetingType}_radio`}
              value={TARGETING_OP.NONE}
              checked={radioValue === TARGETING_OP.NONE}
              onChange={() => this.handleChangeRadio(TARGETING_OP.NONE)}
            />{" "}
            {noneText}
          </label>

          <label
            htmlFor={`${targetingType}_radio_include`}
            className="cursor-pointer"
          >
            <input
              id={`${targetingType}_radio_include`}
              type="radio"
              name={`${targetingType}_radio`}
              value={TARGETING_OP.INCLUDES}
              checked={radioValue === TARGETING_OP.INCLUDES}
              onChange={() => this.handleChangeRadio(TARGETING_OP.INCLUDES)}
            />{" "}
            {includesText}
          </label>
        </div>

        {radioValue !== "NONE" && TargetingSelector}
      </>
    );
  }
}

const DEMAND_SOURCE_OPTIONS_LABELS = {
  [DEMAND_SOURCE_TYPE.ALL]: "Apply to all bidders",
  [DEMAND_SOURCE_TYPE.SPECIFIC]: "Apply to specific bidders",
};

class DemandSourceControl extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      options: _.values(DEMAND_SOURCE_TYPE).map((val) => ({
        label: DEMAND_SOURCE_OPTIONS_LABELS[val],
        value: val,
      })),
      dsList: [],
      bidderByDs: {},
    };

    this.handleOptionsChanged = this.handleOptionsChanged.bind(this);
    this.handleDemandSourceChanged = this.handleDemandSourceChanged.bind(this);
    this.handleBidderChanged = this.handleBidderChanged.bind(this);
    this.handleBidderSelected = this.handleBidderSelected.bind(this);
    this.handleUpdateData = this.handleUpdateData.bind(this);
  }

  componentDidMount() {
    const { demandSource } = this.props;

    const dsList = _.map(
      _.orderBy(demandSource, (item) => item.bidders.length, "desc"),
      "demandSource"
    );

    this.setState({
      dsList,
      bidderByDs: _.keyBy(demandSource, "demandSource"),
    });
  }

  handleOptionsChanged(value) {
    const { dsList } = this.state;
    switch (value) {
      case DEMAND_SOURCE_TYPE.SPECIFIC:
        this.handleUpdateData(
          _.mapValues(_.keyBy(dsList), () => ({ all: true }))
        );
        break;
      case DEMAND_SOURCE_TYPE.ALL:
        this.handleUpdateData(null);
        break;
      default:
        break;
    }
  }

  handleDemandSourceChanged(ds, val) {
    const { targeting } = this.props;
    let newTargeting = {
      ...targeting,
    };

    if (val) {
      newTargeting[ds] = { all: true };
    } else {
      newTargeting = _.omit(newTargeting, ds);
    }

    this.handleUpdateData(newTargeting);
  }

  handleBidderChanged(ds, val) {
    const { targeting } = this.props;
    let newTargeting = {
      ...targeting,
    };

    if (val) {
      newTargeting[ds] = { all: true };
    } else {
      newTargeting[ds] = { all: false, ids: [] };
    }

    this.handleUpdateData(newTargeting);
  }

  handleBidderSelected(ds, selectedBidders) {
    const { targeting } = this.props;
    let newTargeting = {
      ...targeting,
    };

    const ids = _.map(selectedBidders, (item) => item.value);
    newTargeting[ds] = { all: false, ids };

    this.handleUpdateData(newTargeting);
  }

  handleUpdateData(targeting) {
    const { handleChanged } = this.props;
    if (handleChanged) {
      handleChanged(targeting);
    }
  }

  render() {
    const { targeting } = this.props;
    const { options, bidderByDs, dsList } = this.state;

    const dsType = getDemandSourceType(targeting);

    return (
      <>
        <RadioControl
          options={options}
          value={dsType}
          onChange={this.handleOptionsChanged}
        ></RadioControl>
        {dsType === DEMAND_SOURCE_TYPE.SPECIFIC && !_.isEmpty(bidderByDs) && (
          <div className="border flex flex-col gap-2 border-gray-400 px-3 py-2">
            {dsList.map((ds) => {
              const ids = _.get(targeting, [ds, "ids"], []);
              const bidders = _.get(bidderByDs, [ds, "bidders"], []);
              const selectedBidders = _.map(ids, (id) => {
                const bidder = _.find(bidders, { id: id });
                return bidder
                  ? { label: bidder.name, value: id }
                  : { label: id, value: id };
              });

              return (
                <div key={ds}>
                  <label className="rounded inline-flex cursor-pointer items-center">
                    <Checkbox
                      isChecked={!!targeting[ds]}
                      onChange={(val) =>
                        this.handleDemandSourceChanged(ds, val)
                      }
                    ></Checkbox>
                    <span className="ml-1 align-middle text-sm font-semibold text-gray-800 hover:text-gray-700">
                      {_.startCase(ds)}
                    </span>
                  </label>
                  {bidders.length > 0 && !!targeting[ds] && (
                    <div className="ml-6">
                      <RadioControl
                        options={[
                          { label: "All", value: true },
                          { label: "Specific Bidders", value: false },
                        ]}
                        value={_.get(targeting, [ds, "all"], false)}
                        onChange={(val) => this.handleBidderChanged(ds, val)}
                      ></RadioControl>
                    </div>
                  )}
                  {bidders.length > 0 &&
                    !!targeting[ds] &&
                    targeting[ds].all === false && (
                      <Select
                        className="ml-6 mt-1 w-1/2"
                        isSearchable={true}
                        isClearable={true}
                        closeMenuOnSelect={false}
                        isMulti={true}
                        value={selectedBidders}
                        onChange={(value) =>
                          this.handleBidderSelected(ds, value)
                        }
                        options={bidders.map((bidder) => ({
                          label: bidder.name,
                          value: bidder.id,
                        }))}
                      ></Select>
                    )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  }
}

export default BrandPriceSettingControl;
